import HeroBanner from '@quicktools/shared-components/components/landingPages/HeroBanner';
import IconPlusLarge from '@picsart/icons/IconPlusLarge';
import { SetStateAction, useContext, useEffect, useState, Dispatch } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  COLOR_PALETTE_FROM_IMAGE,
  CardImageSection,
  DemoImages,
  FaqCollapsedSection,
  SimplifiedStepSection,
  UploadAlert,
  UploadButton,
  ImageWithTextSection,
  COLOR_WHEEL_TOOL,
  COLOR_PICKER_TOOL,
  COLOR_CODES_TOOL,
  ToolRating,
  WithLanding,
  ToolCategory,
} from '@quicktools/shared-components';
import { CardImageMediaTypes } from '@quicktools/shared-components/components/landingPages/base/CardImage';
import { validateFileFormat } from '@quicktools/shared-components/utils/image';

import { appConfig } from '../../configs';
import { AppContext } from '../../services/context/AppContext';
import { convertImageFilesToBlobs, getPathFromUrl } from '../../utils/imageHelper';
import UploadImageArea from '../../components/UploadImageArea';
import { UploadType } from '../../utils/types';
import { ACCEPTED_FORMATS } from '../../utils/constants';
import analytics from '../../services/analytics';
import { UploadFilePosition } from '../../utils/enums';
import { PageType } from '../../services/analytics/types';

import useStyles from './styles';

const TOOLS = [COLOR_WHEEL_TOOL, COLOR_PICKER_TOOL, COLOR_CODES_TOOL];

const BREADCRUMB_ITEMS = [
  {
    text: 'Color',
    href: '/color/',
  },
  {
    text: 'Palette from Image',
    href: './',
  },
];

function Home({ setShowEditorPage }: { setShowEditorPage: Dispatch<SetStateAction<boolean>> }) {
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const classes = useStyles({ showAlert: showAlert });
  const { t } = useTranslation();
  const { accept, multiple, suggestedImgData } = appConfig;

  const { dispatch } = useContext(AppContext);

  useEffect(() => {
    analytics.landingPage();
  }, []);

  const selectImage = async (url: string) => {
    const src = getPathFromUrl(url);
    const newSources = await convertImageFilesToBlobs([src]);
    const imgSrc = newSources ? newSources[0].url : '';
    dispatch({
      type: 'UPLOAD_IMAGE',
      payload: {
        imgSrc: imgSrc,
      },
    });

    setShowEditorPage(true);
  };

  const handleUpload = async (files: File[], position?: number, uploadType?: UploadType) => {
    if (!files?.length) {
      return;
    }
    const validationResult = validateFileFormat(files[0], ACCEPTED_FORMATS);

    analytics.fileUpload({
      position: position ?? UploadFilePosition.ALERT,
      file_format: validationResult.fileType,
      upload_type: uploadType ?? 'button',
      pageName: PageType.LANDING_PAGE,
    });

    if (validationResult.isValid) {
      setShowAlert(false);

      const newSources = await convertImageFilesToBlobs(files);
      const imgSrc = newSources ? newSources[0].url : '';

      dispatch({
        type: 'UPLOAD_IMAGE',
        payload: {
          imgSrc: imgSrc,
        },
      });

      setShowEditorPage(true);
    } else {
      setShowAlert(true);
    }
  };

  return (
    <WithLanding
      analytics={analytics}
      toolCategory={ToolCategory.Image}
      currentTool={COLOR_PALETTE_FROM_IMAGE}
      similarToolsParagraph={t('home.similarTools.paragraph')}
      similarTools={TOOLS}
      breadcrumbItems={BREADCRUMB_ITEMS}
    >
      <UploadAlert
        accept={accept}
        multiple={multiple}
        description={t('home.uploadAlertText')}
        className={classes.alert}
        onClose={() => {
          setShowAlert(false);
        }}
        handleUpload={handleUpload}
      />

      <HeroBanner
        partialBg
        className={classes.heroBanner}
        imgContainerClassName={classes.imageContainer}
        heading={t('home.heroBanner.heading')}
        content={t('home.heroBanner.content')}
        imgAlt={t('home.heroBanner.imgAlt')}
        imgSrc="https://cdn140.picsart.com/79285405729617516708.png"
        footer={
          <>
            <div>
              <UploadImageArea handleUpload={handleUpload} />
              <DemoImages
                className={classes.demoArea}
                images={[...suggestedImgData]}
                text={t('home.demoImagesText')}
                onImageClick={selectImage}
              />
            </div>
            <ToolRating review={4.4} reviewsNumber={28}></ToolRating>
          </>
        }
      />
      <ImageWithTextSection
        textContainerClassName={classes.textContainerClassName}
        className={classes.imageWithTextSection}
        imgSrc="https://cdn140.picsart.com/80822124148298068188.png"
        imgAlt={t('home.imageWithText.ingAlt')}
        title={t('home.imageWithText.title')}
        description={<Trans i18nKey={'home.imageWithText.description'} components={{ a: <a /> }} />}
      />
      <SimplifiedStepSection
        headingElement={'h2'}
        stepHeadingElement={'h3'}
        title={t('home.simplifiedStepSection.title')}
        steps={[
          {
            title: t('home.simplifiedStepSection.steps.0.title'),
            description: t('home.simplifiedStepSection.steps.0.description'),
          },
          {
            title: t('home.simplifiedStepSection.steps.1.title'),
            description: t('home.simplifiedStepSection.steps.1.description'),
          },
          {
            title: t('home.simplifiedStepSection.steps.2.title'),
            description: t('home.simplifiedStepSection.steps.2.description'),
          },
        ]}
        cta={
          <UploadButton
            accept={accept}
            multiple={multiple}
            className={classes.button}
            size={'large'}
            onChange={(files) => handleUpload(files, UploadFilePosition.BUTTON, 'button')}
          >
            <IconPlusLarge />
            {t('home.heroBanner.buttonText')}
          </UploadButton>
        }
      />
      <CardImageSection
        title={t('home.cardImageSection.title')}
        items={[
          {
            title: t('home.cardImageSection.steps.0.title'),
            description: t('home.cardImageSection.steps.0.description'),
            media: {
              type: CardImageMediaTypes.Image,
              images: [
                {
                  src: 'https://cdn140.picsart.com/21888234052993518365.svg',
                  alt: t('home.cardImageSection.securityIconAlt'),
                },
              ],
            },
          },
          {
            title: t('home.cardImageSection.steps.1.title'),
            description: t('home.cardImageSection.steps.1.description'),
            media: {
              type: CardImageMediaTypes.Image,
              images: [
                {
                  src: 'https://cdn140.picsart.com/44196028685084740523.svg',
                  alt: t('home.cardImageSection.noSoftwareIconAlt'),
                },
              ],
            },
          },
          {
            title: t('home.cardImageSection.steps.2.title'),
            description: t('home.cardImageSection.steps.2.description'),
            media: {
              type: CardImageMediaTypes.Image,
              images: [
                {
                  src: 'https://cdn140.picsart.com/67684046627063369020.svg',
                  alt: t('home.cardImageSection.highQualityIconAlt'),
                },
              ],
            },
          },
        ]}
      />
      <FaqCollapsedSection
        title={t('home.faqSection.title')}
        description={
          <>
            {t('home.faqSection.description')}
            <a
              className={classes.boldText}
              target="_blank"
              href="https://support.picsart.com/hc/en-us/requests/new"
              rel="noreferrer"
            >
              {' '}
              {t('home.faqSection.coloredDescription')}
            </a>
          </>
        }
        questions={[
          {
            question: t('home.faqSection.questions.0.question'),
            answer: t('home.faqSection.questions.0.answer'),
          },
          {
            question: t('home.faqSection.questions.1.question'),
            answer: t('home.faqSection.questions.1.answer'),
          },
          {
            question: 'Do you have more image tools and functionalities?',
            answer: (
              <div>
                Yes, you can check out all{' '}
                <a href="https://tools.picsart.com/image/" target="_blank" rel="noreferrer">
                  image tools
                </a>{' '}
                by clicking ‘Image’ in our header, or you can go to Picsart’s all-in-one image
                editor{' '}
                <a href="https://picsart.com/photo-editor" target="_blank" rel="noreferrer">
                  here
                </a>
                .
              </div>
            ),
            answerSchema:
              'Yes, you can check out all image tools by clicking ‘Image’ in our header, or you can go to Picsart’s all-in-one image editor <a href="https://picsart.com/photo-editor" target="_blank">here</a>.',
          },
        ]}
      />
    </WithLanding>
  );
}

export default Home;
